import type { AIDifficulty } from '../../shared/ai';
import {
  type EngineError,
  ErrorCode,
  type GameState,
  type PlayerId,
  type Result,
} from '../../shared/types/domain';
import { TOAST } from '../messages/toasts';
import {
  resetReconnectAttempts,
  setAIDifficulty,
  setGameCode,
  setIsLocalGame,
  setLatencyMs,
  setOnboardingEntry,
  setOpponentDisconnectDeadlineMs,
  setPlayerId,
  setReconnectOverlayState,
  setScenario,
  setSpectatorMode,
  setTransport,
  setWaitingScreenState,
} from './client-context-store';
import { clearClientGameState } from './game-state-store';
import type { StoredLocalGameSession } from './local-session-store';
import { deriveGameStartClientState } from './network';
import type { ClientState } from './phase';
import type { ClientSession } from './session-model';
import type { GameTransport } from './transport';

export interface CreatedGameSessionDeps {
  ctx: ClientSession;
  storePlayerToken: (code: string, token: string) => void;
  replaceRoute: (route: string) => void;
  buildGameRoute: (code: string) => string;
  connect: (code: string) => void;
  setWaitingScreenState: (
    state: import('../ui/screens').WaitingScreenState | null,
  ) => void;
  setState: (state: ClientState) => void;
  trackGameCreated: (details: {
    scenario: string;
    mode: 'multiplayer';
  }) => void;
}

export interface LocalGameSessionDeps {
  ctx: ClientSession;
  createLocalTransport: () => GameTransport;
  createLocalGameState: (scenario: string) => Result<GameState, EngineError>;
  getScenarioName: (scenario: string) => string;
  resetTurnTelemetry: () => void;
  clearTrails: () => void;
  clearLog: () => void;
  setChatEnabled: (enabled: boolean) => void;
  logText: (text: string) => void;
  trackGameCreated: (details: {
    scenario: string;
    mode: 'local';
    difficulty: AIDifficulty;
  }) => void;
  applyGameState: (state: GameState) => void;
  logScenarioBriefing: () => void;
  showScenarioBriefing: (state: GameState, playerId: PlayerId) => void;
  setState: (state: ClientState) => void;
  runLocalAI: () => void;
}

export interface SpectateGameSessionDeps {
  ctx: Pick<
    ClientSession,
    | 'gameCode'
    | 'spectatorMode'
    | 'isLocalGame'
    | 'reconnectOverlayState'
    | 'opponentDisconnectDeadlineMs'
    | 'waitingScreenState'
  >;
  resetTurnTelemetry: () => void;
  replaceRoute: (route: string) => void;
  buildGameRoute: (code: string) => string;
  connect: (code: string) => void;
  setWaitingScreenState: (
    state: import('../ui/screens').WaitingScreenState | null,
  ) => void;
  setState: (state: ClientState) => void;
}

// Deps for viewing an archived match replay without a live WebSocket. The
// client applies the match's final state (so the game-over / replay UI has
// real data to render), then hands the full timeline to the replay
// controller which scrubs the match turn-by-turn.
export interface ArchivedReplaySessionDeps {
  ctx: Pick<
    ClientSession,
    | 'gameCode'
    | 'spectatorMode'
    | 'isLocalGame'
    | 'reconnectOverlayState'
    | 'opponentDisconnectDeadlineMs'
    | 'waitingScreenState'
  >;
  resetTurnTelemetry: () => void;
  replaceRoute: (route: string) => void;
  buildGameRoute: (code: string) => string;
  setWaitingScreenState: (
    state: import('../ui/screens').WaitingScreenState | null,
  ) => void;
  setState: (state: ClientState) => void;
  fetchArchivedReplay: (
    code: string,
    gameId: string,
    signal?: AbortSignal,
  ) => Promise<import('../../shared/replay').ReplayTimeline | null>;
  applyGameState: (state: GameState) => void;
  startArchivedReplay: (
    timeline: import('../../shared/replay').ReplayTimeline,
  ) => void;
  clearLog: () => void;
  setChatEnabled: (enabled: boolean) => void;
  logText: (text: string, cssClass?: string) => void;
  showToast: (message: string, type: 'error' | 'info' | 'success') => void;
  exitToMenu: () => void;
  setScenario: (scenario: string) => void;
}

export interface JoinGameSessionDeps {
  ctx: Pick<
    ClientSession,
    | 'gameCode'
    | 'spectatorMode'
    | 'isLocalGame'
    | 'reconnectOverlayState'
    | 'opponentDisconnectDeadlineMs'
    | 'waitingScreenState'
  >;
  getStoredPlayerToken: (code: string) => string | null;
  storePlayerToken: (code: string, token: string) => void;
  resetTurnTelemetry: () => void;
  replaceRoute: (route: string) => void;
  buildGameRoute: (code: string) => string;
  connect: (code: string) => void;
  setWaitingScreenState: (
    state: import('../ui/screens').WaitingScreenState | null,
  ) => void;
  setState: (state: ClientState) => void;
  validateJoin: (
    code: string,
    playerToken: string | null,
  ) => Promise<Result<string | null, { message: string; code?: ErrorCode }>>;
  showToast: (message: string, type: 'error' | 'info' | 'success') => void;
  exitToMenu: () => void;
  selectCodeInput?: () => void;
  // When the server reports the room is full (ROOM_FULL), fall back to a
  // watch-only spectator session instead of bouncing to the menu. Callers
  // that don't support spectator fallback can omit this — the join will exit
  // to menu on ROOM_FULL, preserving legacy behaviour.
  fallbackToSpectator?: (code: string) => void;
}

export interface ExitToMenuSessionDeps {
  ctx: Pick<
    ClientSession,
    | 'gameCode'
    | 'gameState'
    | 'isLocalGame'
    | 'latencyMs'
    | 'playerId'
    | 'reconnectOverlayState'
    | 'reconnectAttempts'
    | 'opponentDisconnectDeadlineMs'
    | 'onboardingEntry'
    | 'spectatorMode'
    | 'transport'
    | 'trainingMovementFeedback'
    | 'waitingScreenState'
  >;
  stopPing: () => void;
  stopTurnTimer: () => void;
  closeConnection: () => void;
  resetTurnTelemetry: () => void;
  replaceRoute: (route: string) => void;
  setState: (state: ClientState) => void;
}

const completeLocalGameSession = (
  deps: LocalGameSessionDeps,
  scenario: string,
  state: GameState,
  playerId: PlayerId,
  mode: 'new' | 'restored',
): void => {
  setSpectatorMode(deps.ctx, false);
  setIsLocalGame(deps.ctx, true);
  setGameCode(deps.ctx, null);
  setLatencyMs(deps.ctx, -1);
  resetReconnectAttempts(deps.ctx);
  setWaitingScreenState(deps.ctx, null);
  setReconnectOverlayState(deps.ctx, null);
  setOpponentDisconnectDeadlineMs(deps.ctx, null);
  setScenario(deps.ctx, scenario);
  setPlayerId(deps.ctx, playerId);
  deps.resetTurnTelemetry();
  setTransport(deps.ctx, deps.createLocalTransport());
  deps.clearTrails();
  deps.clearLog();
  deps.setChatEnabled(false);
  deps.logText(
    `${mode === 'restored' ? 'Restored vs AI' : 'vs AI'} (${deps.ctx.aiDifficulty}) \u2014 ${deps.getScenarioName(scenario)}`,
  );

  if (mode === 'new') {
    deps.trackGameCreated({
      scenario,
      mode: 'local',
      difficulty: deps.ctx.aiDifficulty,
    });
  }

  deps.applyGameState(state);
  deps.logScenarioBriefing();

  const gameState = deps.ctx.gameState;

  if (!gameState) {
    return;
  }

  const nextState = deriveGameStartClientState(gameState, deps.ctx.playerId);

  deps.setState(nextState);
  if (
    mode === 'new' &&
    deps.ctx.playerId >= 0 &&
    nextState !== 'playing_fleetBuilding'
  ) {
    deps.showScenarioBriefing(gameState, deps.ctx.playerId as PlayerId);
  }

  if (nextState === 'playing_opponentTurn') {
    deps.runLocalAI();
  }
};

export const completeCreatedGameSession = (
  deps: CreatedGameSessionDeps,
  scenario: string,
  code: string,
  playerToken: string,
): void => {
  setSpectatorMode(deps.ctx, false);
  setIsLocalGame(deps.ctx, false);
  setReconnectOverlayState(deps.ctx, null);
  setOpponentDisconnectDeadlineMs(deps.ctx, null);
  setWaitingScreenState(deps.ctx, null);
  setScenario(deps.ctx, scenario);
  setGameCode(deps.ctx, code);
  deps.setWaitingScreenState({
    kind: 'private',
    code,
    connecting: false,
  });
  deps.storePlayerToken(code, playerToken);
  deps.replaceRoute(deps.buildGameRoute(code));
  deps.trackGameCreated({
    scenario,
    mode: 'multiplayer',
  });
  deps.setState('waitingForOpponent');
  deps.connect(code);
};

export const startLocalGameSession = (
  deps: LocalGameSessionDeps,
  scenario: string,
): void => {
  const forcedSide = (globalThis as Record<string, unknown>)
    .__DELTAV_FORCE_PLAYER_SIDE;
  const humanSide = (
    forcedSide === 0 || forcedSide === 1
      ? forcedSide
      : Math.random() < 0.5
        ? 0
        : 1
  ) as PlayerId;

  const result = deps.createLocalGameState(scenario);

  if (!result.ok) {
    throw new Error(result.error.message);
  }

  completeLocalGameSession(deps, scenario, result.value, humanSide, 'new');
};

export const resumeLocalGameSession = (
  deps: LocalGameSessionDeps,
  snapshot: StoredLocalGameSession,
): void => {
  setAIDifficulty(deps.ctx, snapshot.aiDifficulty);
  setOnboardingEntry(deps.ctx, snapshot.onboardingEntry ?? null);
  completeLocalGameSession(
    deps,
    snapshot.scenario,
    snapshot.gameState,
    snapshot.playerId,
    'restored',
  );
};

export const beginSpectateGameSession = (
  deps: SpectateGameSessionDeps,
  code: string,
): void => {
  setSpectatorMode(deps.ctx as ClientSession, true);
  setIsLocalGame(deps.ctx, false);
  setReconnectOverlayState(deps.ctx, null);
  setOpponentDisconnectDeadlineMs(deps.ctx, null);
  setWaitingScreenState(deps.ctx, null);
  deps.resetTurnTelemetry();
  setGameCode(deps.ctx, code);
  deps.setWaitingScreenState({
    kind: 'private',
    code,
    connecting: true,
  });
  deps.replaceRoute(deps.buildGameRoute(code));
  deps.setState('connecting');
  deps.connect(code);
};

// Enter archived-replay mode: fetch the timeline via the spectator route
// (no playerToken), apply the match's final state so the game-over / replay
// overlay has real data, then hand the timeline to the replay controller.
// Deliberately skips connect(code) — archived replays do not open a
// WebSocket.
export const beginArchivedReplaySession = async (
  deps: ArchivedReplaySessionDeps,
  code: string,
  gameId: string,
  fetchSignal?: AbortSignal,
): Promise<void> => {
  setSpectatorMode(deps.ctx as ClientSession, true);
  setIsLocalGame(deps.ctx, false);
  setReconnectOverlayState(deps.ctx, null);
  setOpponentDisconnectDeadlineMs(deps.ctx, null);
  setWaitingScreenState(deps.ctx, null);
  deps.resetTurnTelemetry();
  setGameCode(deps.ctx, code);
  deps.replaceRoute(deps.buildGameRoute(code));
  deps.setState('connecting');
  deps.setWaitingScreenState({
    kind: 'private',
    code,
    connecting: true,
  });

  const timeline = await deps.fetchArchivedReplay(code, gameId, fetchSignal);

  if (fetchSignal?.aborted) {
    return;
  }

  if (!timeline || timeline.entries.length === 0) {
    deps.showToast(TOAST.sessionController.replayUnavailable, 'error');
    deps.exitToMenu();
    return;
  }

  // Seed scenario so the UI's scenario label renders correctly.
  deps.setScenario(timeline.scenario);
  deps.clearLog();
  deps.setChatEnabled(false);
  deps.logText(`Replay: ${timeline.gameId}`, 'log-system');

  // Apply the final state first — this populates gameState with the
  // match's endgame (winner, outcome, turn count) so the replay-controller
  // has a "source state" to restore when the viewer closes the replay.
  const lastEntry = timeline.entries[timeline.entries.length - 1];
  if (lastEntry) {
    deps.applyGameState(lastEntry.message.state);
  }

  deps.setWaitingScreenState(null);
  deps.setState('gameOver');
  deps.startArchivedReplay(timeline);
};

export const beginJoinGameSession = async (
  deps: JoinGameSessionDeps,
  code: string,
  playerToken: string | null = null,
): Promise<void> => {
  const effectiveToken = playerToken ?? deps.getStoredPlayerToken(code);
  const validation = await deps.validateJoin(code, effectiveToken);

  if (!validation.ok) {
    if (
      validation.error.code === ErrorCode.ROOM_FULL &&
      deps.fallbackToSpectator
    ) {
      deps.showToast(TOAST.sessionController.joinRoomFullSpectator, 'info');
      deps.fallbackToSpectator(code);
      return;
    }
    deps.showToast(validation.error.message, 'error');
    deps.selectCodeInput?.();
    deps.exitToMenu();
    return;
  }

  setSpectatorMode(deps.ctx as ClientSession, false);
  setIsLocalGame(deps.ctx, false);
  setReconnectOverlayState(deps.ctx, null);
  setOpponentDisconnectDeadlineMs(deps.ctx, null);
  setWaitingScreenState(deps.ctx, null);

  if (validation.value) {
    deps.storePlayerToken(code, validation.value);
  }
  deps.resetTurnTelemetry();
  setGameCode(deps.ctx, code);
  deps.setWaitingScreenState({
    kind: 'private',
    code,
    connecting: true,
  });
  deps.replaceRoute(deps.buildGameRoute(code));
  deps.setState('connecting');
  deps.connect(code);
};

export const exitToMenuSession = (deps: ExitToMenuSessionDeps): void => {
  deps.stopPing();
  deps.stopTurnTimer();
  deps.closeConnection();
  deps.resetTurnTelemetry();
  clearClientGameState(deps.ctx);
  setGameCode(deps.ctx, null);
  setLatencyMs(deps.ctx, -1);
  resetReconnectAttempts(deps.ctx);
  setWaitingScreenState(deps.ctx, null);
  setSpectatorMode(deps.ctx, false);
  setIsLocalGame(deps.ctx, false);
  setReconnectOverlayState(deps.ctx, null);
  setOpponentDisconnectDeadlineMs(deps.ctx, null);
  setOnboardingEntry(deps.ctx, null);
  deps.ctx.trainingMovementFeedback = null;
  setPlayerId(deps.ctx, -1);
  setTransport(deps.ctx, null);
  deps.replaceRoute('/');
  deps.setState('menu');
};
